import * as React from "react"
import { cn } from "@/lib/utils"
import { Button } from "./button"
import { Upload, FileText, X } from "lucide-react"

interface FileInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label?: string
  placeholder?: string
  onFileSelect?: (file: File | null) => void
  selectedFile?: File | null
  accept?: string
  className?: string
  buttonVariant?: "default" | "outline" | "secondary" | "ghost" | "link" | "destructive"
  buttonSize?: "default" | "sm" | "lg" | "icon"
}

const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(
  ({ 
    label, 
    placeholder = "Selecionar arquivo...", 
    onFileSelect, 
    selectedFile, 
    accept, 
    className,
    buttonVariant = "outline",
    buttonSize = "default",
    ...props 
  }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null)

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] || null
      onFileSelect?.(file)
    }

    const handleButtonClick = () => {
      inputRef.current?.click()
    }

    const handleRemoveFile = () => {
      onFileSelect?.(null)
      if (inputRef.current) {
        inputRef.current.value = ''
      }
    }

    return (
      <div className={cn("space-y-2", className)}>
        {label && (
          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            {label}
          </label>
        )}
        
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleFileChange}
            className="hidden"
            {...props}
          />
          
          <Button
            type="button"
            variant={buttonVariant}
            size={buttonSize}
            onClick={handleButtonClick}
            className="flex-1"
          >
            <Upload className="h-4 w-4 mr-2" />
            {selectedFile ? selectedFile.name : placeholder}
          </Button>
          
          {selectedFile && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRemoveFile}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {selectedFile && (
          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-sm text-green-700 border border-green-200">
            <FileText className="h-4 w-4" />
            <span className="font-medium">{selectedFile.name}</span>
            <span className="text-green-600">
              ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
            </span>
          </div>
        )}
      </div>
    )
  }
)

FileInput.displayName = "FileInput"

export { FileInput } 